import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import ManageView from "./tabs/ManageView";
import { ViewStateInspection } from "./tabs/ViewStateInspection";
import { TimescalesTab } from "./tabs/TimescalesTab";
import { SettingsTab } from "./tabs/SettingsTab";
import { Calendar, X, Layers, Save, Trash, Settings, SlidersHorizontal, Share2 } from "lucide-react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { getViewStateIconByRecord } from "@/other/utils/utils";
import { Button } from "@/modern/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { useViewEditor, ViewEditorProvider } from "./context/ViewEditorContext";
import { View } from "@/bryntum-lib/models/View";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import { ShareView } from "./tabs/ShareView";
import { VIEW_EDITOR_SHELL_CLASS } from "./components/ViewEditorSection";
import { cn } from "@/modern/utils";

const ViewEditorFooter = () => {
  const { record, isSaveAsView } = useViewEditor();

  return (
    <div className="flex flex-wrap justify-between items-center gap-3 w-full text-[11px] text-muted-foreground/70 px-1">
      <div className="flex items-center gap-2">
        <span className="inline-block w-1 h-1 rounded-full bg-muted-foreground/30" />
        <p className="m-0">
          Created by <span className="text-foreground/80 font-medium">{record?.isDefaultView ? "Swivu" : record?.creatorUserName}</span>
        </p>
      </div>
      {!record?.isOwnView && !isSaveAsView && (
        <p className="m-0 text-amber-600/90 dark:text-amber-500/90 flex items-center gap-1.5">
          <span className="inline-block w-1 h-1 rounded-full bg-amber-600 dark:bg-amber-500" />
          {record?.creatorUserName || "N/A"} is the only user that can save changes
        </p>
      )}
    </div>
  );
};

export const SwivuViewEditorContent = () => {
  const context = useSwivuBryntumContext();
  const { project } = context;
  const {
    onRemoveView,
    onSaveView,
    isSaveAsView,
    isDeleteDisabled,
    isSaveDisabled,
    record,
    onClose,
    editorInstance,
    canUseRecordViewState,
    activeTab,
    setActiveTab
  } = useViewEditor();
  const viewIcon = getViewStateIconByRecord(record, project);
  const isSchedulerView = record?.viewType === VIEW_TYPES.schedulerView;

  return (
    <BaseModal
      title="View Editor"
      description=""
      onClose={onClose}
      size={{
        minWidth: "min(48rem, 95vw)",
        minHeight: "min(48rem, 90vh)",
        width: "48rem",
        height: "48rem",
        maxWidth: "95vw",
        maxHeight: "90vh"
      }}
      editorInstance={editorInstance}
      record={record}
      headerIconCls=""
      modal={true}
      version=""
      hideMenu={true}
      footerRenderer={() => <ViewEditorFooter />}
      footerClassName="justify-between items-center !px-6 !py-3 border-t border-border/40 bg-muted/20"
      headerRenderer={() => (
        <div className="drag-handle border-b border-border/40 relative bg-background">
          {/* Subtle accent line */}
          <div
            className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"
            style={record?.color ? {
              backgroundImage: `linear-gradient(90deg, transparent, ${record.color}40, transparent)`
            } : undefined}
          />

          {/* Header content */}
          <div className="flex items-center justify-between px-6 py-3.5">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <i
                className={cn(viewIcon, "text-base shrink-0")}
                style={record?.color ? { color: record.color } : undefined}
              />
              <div className="min-w-0 flex-1">
                <h2
                  className="text-[15px] font-medium text-foreground m-0 leading-tight truncate"
                  style={record?.color ? { color: record.color } : undefined}
                >
                  {record?.name}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0 ml-4">
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  "inline-flex items-center gap-1.5 text-[13px] font-normal leading-none [&_svg]:size-3.5 [&_svg]:shrink-0",
                  "text-destructive/90 hover:text-destructive hover:bg-destructive/10",
                  "transition-all duration-200"
                )}
                onClick={onRemoveView}
                disabled={isDeleteDisabled || !record}
              >
                <Trash aria-hidden />
                <span>Delete</span>
              </Button>
              <div className="w-px h-4 bg-border/50" />
              <Button
                size="sm"
                className={cn(
                  "inline-flex items-center gap-1.5 text-[13px] font-medium leading-none [&_svg]:size-3.5 [&_svg]:shrink-0",
                  "shadow-sm hover:shadow transition-all duration-200"
                )}
                onClick={onSaveView}
                disabled={isSaveDisabled || !record}
              >
                <Save aria-hidden />
                <span>{isSaveAsView ? "Save As" : "Save"}</span>
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                aria-label="Close"
                className="hover:bg-muted/80 transition-colors duration-200"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    >
      <BaseModalBody
        contentElementClassName="bg-background"
        className="flex flex-col h-full min-h-0 !p-6"
      >
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col h-full overflow-hidden"
        >
          {/* Tab Navigation - Left-aligned with horizontal scroll */}
          <TabsList className="mb-4 shrink-0 w-full justify-start overflow-x-auto gap-2">
            <TabsTrigger value="view-details" className="gap-2 flex-grow basis-32 px-4">
              <Settings className="w-3.5 h-3.5" />
              General
            </TabsTrigger>
            <TabsTrigger value="share-view" className="gap-2 flex-grow basis-32 px-4">
              <Share2 className="w-3.5 h-3.5" />
              Share View
            </TabsTrigger>
            <TabsTrigger value="state-configurations" className="gap-2 flex-grow basis-32 px-4">
              <Layers className="w-3.5 h-3.5" />
              Inspect State
            </TabsTrigger>
            {isSchedulerView && (
              <>
                <TabsTrigger value="timescales" className="gap-2 flex-grow basis-32 px-4">
                  <Calendar className="w-3.5 h-3.5" />
                  Timescales
                </TabsTrigger>
                <TabsTrigger value="schedule-settings" className="gap-2 flex-grow basis-32 px-4">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Settings
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Scroll Container Wrapper - This creates the scroll boundary */}
          <div className="flex-1 min-h-0 relative">
            <TabsContent value="view-details" className="absolute inset-0 overflow-y-auto data-[state=inactive]:hidden">
              <div className={VIEW_EDITOR_SHELL_CLASS}>
                <ManageView />
              </div>
            </TabsContent>

            <TabsContent
              value="state-configurations"
              className="absolute inset-0 overflow-y-auto data-[state=inactive]:hidden"
            >
              <div className={VIEW_EDITOR_SHELL_CLASS}>
                <ViewStateInspection
                  record={record}
                  context={context}
                  canUseRecordViewState={canUseRecordViewState}
                />
              </div>
            </TabsContent>

            <TabsContent
              value="timescales"
              className="absolute inset-0 flex flex-col overflow-hidden data-[state=inactive]:hidden"
            >
              <div className={cn(VIEW_EDITOR_SHELL_CLASS, "flex flex-col flex-1 min-h-0 h-full")}>
                <TimescalesTab />
              </div>
            </TabsContent>

            <TabsContent
              value="schedule-settings"
              className="absolute inset-0 overflow-y-auto data-[state=inactive]:hidden"
            >
              <div className={VIEW_EDITOR_SHELL_CLASS}>
                <SettingsTab />
              </div>
            </TabsContent>

            <TabsContent value="share-view" className="absolute inset-0 overflow-y-auto data-[state=inactive]:hidden">
              <div className={VIEW_EDITOR_SHELL_CLASS}>
                <ShareView />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </BaseModalBody>
    </BaseModal>
  );
};

export interface ViewEditorProps {
  record: {
    viewRecord?: View;
    canUseRecordViewState?: boolean;
    isSaveAsView?: boolean;
  };
  onClose: () => void;
  editorInstance: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export const ViewEditor = (props: ViewEditorProps) => {
  return (
    <ViewEditorProvider {...props}>
      <SwivuViewEditorContent />
    </ViewEditorProvider>
  );
};

export default ViewEditor;
